/**
 * Database Metadata Extraction Utilities (sl-57s)
 *
 * SOLID: S - Single Responsibility - Only handles database metadata extraction
 * DRY: Shared by all three context mechanisms (paste, fullscreen, focus)
 */

import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
} from '@blocksuite/affine/blocks/database';
import type { Store } from '@blocksuite/store';
import { DATABASE_LARGE_DATA_POLICY } from '@streamlain/database-identity';

import type {
  DatabaseAttachment,
  DatabaseColumn,
  DatabaseVisibleRowWindow,
} from '../components/ai-chat-content/type';
import { finiteBoundsFromCanvasModel } from './canvas-bounds';

/**
 * Virtual audit properties are rendered by database views, but they are not
 * stored as cells in database row shards. Passing them as projected columns
 * makes exact AI reads fail because the backend correctly rejects unknown
 * materialized column IDs.
 */
export const NON_MATERIALIZED_DATABASE_CONTEXT_COLUMN_TYPES = new Set([
  'created-time',
  'created-by',
  'updated-time',
  'updated-by',
]);

export function isMaterializedDatabaseContextColumn(
  column: Pick<DatabaseColumn, 'type'>
): boolean {
  return !NON_MATERIALIZED_DATABASE_CONTEXT_COLUMN_TYPES.has(column.type);
}

export function filterMaterializedDatabaseContextColumns(
  columns: DatabaseColumn[]
): DatabaseColumn[] {
  return columns.filter(isMaterializedDatabaseContextColumn);
}

/**
 * Extract database metadata from a database block
 * Used by: Copy/Paste, Fullscreen Mode, Focus/Selection
 *
 * @param block - Database block (from store.getBlocksByFlavour or selection)
 * @param source - How the database was captured
 * @param priority - Priority level for AI context
 * @returns DatabaseAttachment with metadata
 */
export function extractDatabaseMetadata(
  block: any,
  source: 'paste' | 'fullscreen' | 'focus',
  priority: 'highest' | 'mid' | 'lowest',
  options: { docId?: string } = {}
): DatabaseAttachment {
  const model = block.model ?? block;
  const dataSource = createDatabaseDataSource(model);
  const activeView = dataSource?.viewManager.currentView$.value;
  const activeViewData = activeView?.data$.value as
    | {
        filter?: { conditions?: unknown[] };
        sort?: { sortBy?: unknown[] };
      }
    | undefined;
  const visibleRowWindow = getVisibleRowWindow(dataSource, source);
  const columns = getDatabaseColumns(model, dataSource);
  const materializedColumns = filterMaterializedDatabaseContextColumns(columns);
  const allPropertyIds =
    dataSource?.getAllPropertyIds() ?? columns.map(c => c.id);

  return {
    docId: options.docId,
    blockId: block.id ?? model.id,
    name: getDatabaseTitle(model),
    columns,
    bounds: finiteBoundsFromCanvasModel(model),
    source,
    priority,
    scope: visibleRowWindow ? 'visible-window' : 'metadata-only',
    activeViewId: activeView?.id,
    activeViewType: activeView?.type,
    activeFilterCount: activeViewData?.filter?.conditions?.length ?? 0,
    activeSortCount: activeViewData?.sort?.sortBy?.length ?? 0,
    visibleRowWindow,
    projectedColumnIds: materializedColumns.map(column => column.id),
    totalColumnCount: allPropertyIds.length,
  };
}

/**
 * Get database title from model
 * Returns "Untitled" if the database has no title set
 */
function getDatabaseTitle(model: any): string {
  try {
    const dataSource = createDatabaseBlockDataSource(model);
    const title = dataSource.storageModel.props.title.toString();
    if (title && title.trim()) {
      return title;
    }
  } catch {}

  return 'Untitled';
}

function createDatabaseDataSource(
  model: any
): DatabaseBlockDataSource | undefined {
  try {
    return createDatabaseBlockDataSource(model);
  } catch (e) {
    console.warn('Failed to create database data source:', e);
    return undefined;
  }
}

/**
 * Get database columns from model
 */
function getDatabaseColumns(
  model: any,
  existingDataSource?: DatabaseBlockDataSource
): DatabaseColumn[] {
  try {
    const dataSource =
      existingDataSource ?? createDatabaseBlockDataSource(model);
    const propertyIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;

    return propertyIds.map(id => {
      const propType = dataSource.propertyTypeGet(id);
      const propName = dataSource.propertyNameGet(id);
      return {
        id,
        name: propName ?? id,
        type: propType ?? 'unknown',
      };
    });
  } catch (e) {
    console.warn('Failed to get database columns:', e);
    return [];
  }
}

export function getVisibleRowWindowFromModel(
  model: any
): DatabaseVisibleRowWindow | undefined {
  return getVisibleRowWindow(createDatabaseDataSource(model));
}

type DatabaseContextView = NonNullable<
  DatabaseBlockDataSource['viewManager']['currentView$']['value']
>;

export function resolveDatabaseContextRowWindow(
  view: DatabaseContextView,
  source?: 'paste' | 'fullscreen' | 'focus'
) {
  const activeViewData = view.data$.value as
    | { filter?: { conditions?: unknown[] } }
    | undefined;
  const hasSearchOrFilter = Boolean(
    view.search$.value.trim() || activeViewData?.filter?.conditions?.length
  );
  if (source !== 'focus' || !hasSearchOrFilter) {
    return view.getBoundedRowIdsWindow();
  }

  const projection = view.projectRows({
    scope: 'filtered-result',
    window: {
      limit: DATABASE_LARGE_DATA_POLICY.ai.contextRowWindowLimit,
      offset: 0,
    },
  });
  return {
    hasMore:
      projection.window.offset + projection.rowIds.length <
      projection.totalCount,
    limit: projection.window.limit,
    offset: projection.window.offset,
    rowIds: projection.rowIds,
    totalCount: projection.totalCount,
  };
}

function getVisibleRowWindow(
  dataSource?: DatabaseBlockDataSource,
  source?: 'paste' | 'fullscreen' | 'focus'
): DatabaseVisibleRowWindow | undefined {
  if (!dataSource) return undefined;

  try {
    const view = dataSource.viewManager.currentView$.value;
    // Selecting the Database block means the filtered Database is the context,
    // not whichever 25-row page happened to be current when the chip mounted.
    // Fullscreen/paste contexts keep their existing bounded-page semantics.
    const window = view
      ? resolveDatabaseContextRowWindow(view, source)
      : undefined;
    if (!window) return undefined;
    const rowIds = window.rowIds.slice(
      0,
      DATABASE_LARGE_DATA_POLICY.ai.contextRowWindowLimit
    );

    return {
      offset: window.offset,
      limit: Math.min(
        window.limit,
        DATABASE_LARGE_DATA_POLICY.ai.contextRowWindowLimit
      ),
      totalCount: window.totalCount,
      rowIds,
      hasMore: window.offset + rowIds.length < window.totalCount,
    };
  } catch (e) {
    console.warn('Failed to get visible database row window:', e);
    return undefined;
  }
}

/**
 * Find database block by ID in store
 */
export function findDatabaseById(store: Store, blockId: string): any | null {
  const databases = store.getBlocksByFlavour('affine:database');
  return databases.find(db => db.id === blockId) ?? null;
}

/**
 * Check if a block is a database
 */
export function isDatabaseBlock(block: any): boolean {
  const flavour = block.flavour ?? block.model?.flavour;
  return flavour === 'affine:database';
}

/**
 * sl-28ek: Format selected row indices into a compact label for the context chip.
 * Compresses consecutive ranges: [1,2,3,5,7,8,9] → "Rows 1-3, 5, 7-9"
 * Uses 1-based indices for user display.
 */
export function formatRowLabel(zeroBasedIndices: number[]): string {
  if (zeroBasedIndices.length === 0) return '';
  const sorted = [...zeroBasedIndices].sort((a, b) => a - b);
  const oneBased = sorted.map(i => i + 1);

  const ranges: string[] = [];
  let start = oneBased[0];
  let end = start;

  for (let i = 1; i < oneBased.length; i++) {
    if (oneBased[i] === end + 1) {
      end = oneBased[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = oneBased[i];
      end = start;
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);

  const label = ranges.join(', ');
  return oneBased.length === 1 ? `Row ${label}` : `Rows ${label}`;
}
